import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import SEO from './SEO';

const GameCreate = () => {
  const navigate = useNavigate();
  const [userBooks, setUserBooks] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [gameTitle, setGameTitle] = useState('');
  const [questionsPerBook, setQuestionsPerBook] = useState(1);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchUserBooks = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          navigate('/mypage');
          return;
        }

        const booksRef = collection(db, 'books');
        const q = query(booksRef, where('uid', '==', currentUser.uid));
        const snapshot = await getDocs(q);
        
        const books = [];
        for (const doc of snapshot.docs) {
          const bookData = doc.data();
          // 각 책의 하이라이트 수 확인
          const highlightsRef = collection(db, 'highlights');
          const highlightsQuery = query(
            highlightsRef, 
            where('bookId', '==', bookData.isbn),
            where('uid', '==', currentUser.uid)
          );
          const highlightsSnapshot = await getDocs(highlightsQuery);
          
          if (highlightsSnapshot.docs.length > 0) {
            books.push({
              id: doc.id,
              ...bookData,
              highlightCount: highlightsSnapshot.docs.length
            });
          }
        }

        setUserBooks(books);
      } catch (err) {
        console.error('책 목록 불러오기 오류:', err);
        setError('책 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserBooks();
  }, [navigate]);

  const handleBookToggle = (bookId) => {
    setSelectedBooks(prev => 
      prev.includes(bookId)
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  const handleCreateTest = async () => {
    if (selectedBooks.length < 3) {
      setError('최소 3권의 책을 선택해주세요.');
      return;
    }

    // 선택된 책들의 하이라이트 수 확인
    const selectedBookData = userBooks.filter(book => selectedBooks.includes(book.id));
    const totalAvailableQuestions = selectedBookData.reduce((total, book) => {
      return total + Math.min(questionsPerBook, book.highlightCount);
    }, 0);

    if (totalAvailableQuestions < 3) {
      setError('최소 3문제 이상 출제 가능한 책을 선택해주세요.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const currentUser = auth.currentUser;
      
      // 테스트 데이터 생성
      const testData = {
        creatorId: currentUser.uid,
        creatorName: currentUser.displayName || '익명',
        books: selectedBookData.map(book => ({
          id: book.id,
          title: book.title,
          authors: book.authors,
          thumbnail: book.thumbnail,
          isbn: book.isbn,
          highlightCount: book.highlightCount,
          questionsPerBook: Math.min(questionsPerBook, book.highlightCount) // 실제 출제 가능한 문제 수
        })),
        questionCount: totalAvailableQuestions,
        questionsPerBook: questionsPerBook, // 사용자가 설정한 책당 문제 수
        createdAt: serverTimestamp(),
        participantCount: 0,
        title: gameTitle.trim() || `${currentUser.displayName || '익명'}님의 문장 테스트`
      };

      const testsRef = collection(db, 'tests');
      const docRef = await addDoc(testsRef, testData);

      setSnackbar({ 
        open: true, 
        message: '테스트가 성공적으로 생성되었습니다!', 
        severity: 'success' 
      });

      navigate(`/game/play/${docRef.id}`);
    } catch (err) {
      console.error('테스트 생성 오류:', err);
      setError('테스트 생성 중 오류가 발생했습니다.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SEO 
        title="내가 좋아하는 책 맞추기 | 스냅로그"
        description="책 맞추기 테스트를 만들고, 원하는 친구에게 공유해보세요"
        url="/game/create"
      />
      
      <Box sx={{ 
        maxWidth: '600px', 
        margin: '0 auto', 
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}>
        {/* 헤더 */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          px: 2,
          py: 0.5,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: 'background.paper'
        }}>
          <IconButton onClick={() => navigate('/mypage')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h7" sx={{ fontWeight: 700, fontFamily: 'NanumSquareB' }}>
            문장 테스트 만들기
          </Typography>
        </Box>

        <Box sx={{ p: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {/* 게임 제목 입력 */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'NanumSquareEB' }}>
              테스트 이름
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontFamily: 'NanumSquareR', mb: 2 }}>
              미입력 시, 강해보이는 이름으로 만들어집니다
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <input
                type="text"
                value={gameTitle}
                onChange={(e) => setGameTitle(e.target.value)}
                placeholder="예: 제 1차 독서 경연대회, 제 3차 전투력 측정대회, 제 7차 천하제일 무술대회..."
                maxLength={50}
                style={{
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: 'NanumSquareR',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1a237e';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e0e0e0';
                }}
              />
              <Typography sx={{ 
                fontSize: '0.875rem', 
                color: 'text.secondary',
                fontFamily: 'NanumSquareR'
              }}>
                {gameTitle.length}/50자 (선택사항)
              </Typography>
            </Box>
          </Paper>
          {/* 책당 문제 수 설정 */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'NanumSquareEB' }}>
              문제 수 설정
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontFamily: 'NanumSquareR', mb: 2 }}>
              각 책에서 출제할 문제 수를 설정하세요
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontFamily: 'NanumSquareR', minWidth: '120px' }}>
                  책당 문제 수:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setQuestionsPerBook(Math.max(1, questionsPerBook - 1))}
                    disabled={questionsPerBook <= 1}
                    sx={{
                      minWidth: '40px',
                      height: '40px',
                      borderColor: '#1a237e',
                      color: '#1a237e',
                      '&:hover': { borderColor: '#283593' },
                      '&:disabled': { borderColor: '#e0e0e0', color: '#999' }
                    }}
                  >
                    -
                  </Button>
                  <Typography
                    sx={{
                      minWidth: '60px',
                      textAlign: 'center',
                      fontFamily: 'NanumSquareB',
                      fontSize: '1.1rem',
                      color: '#1a237e'
                    }}
                  >
                    {questionsPerBook}개
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setQuestionsPerBook(Math.min(10, questionsPerBook + 1))}
                    disabled={questionsPerBook >= 10}
                    sx={{
                      minWidth: '40px',
                      height: '40px',
                      borderColor: '#1a237e',
                      color: '#1a237e',
                      '&:hover': { borderColor: '#283593' },
                      '&:disabled': { borderColor: '#e0e0e0', color: '#999' }
                    }}
                  >
                    +
                  </Button>
                </Box>
              </Box>
              
              {/* 예외 상황 안내 */}
              {selectedBooks.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                  <Typography sx={{ 
                    fontSize: '0.875rem', 
                    color: '#666',
                    fontFamily: 'NanumSquareR',
                    mb: 1
                  }}>
                    📋 문제 수 안내:
                  </Typography>
                  <Box sx={{ fontSize: '0.8rem', color: '#666', fontFamily: 'NanumSquareR' }}>
                    {selectedBooks.map(bookId => {
                      const book = userBooks.find(b => b.id === bookId);
                      if (!book) return null;
                      
                      const availableQuestions = Math.min(questionsPerBook, book.highlightCount);
                      const isLimited = book.highlightCount < questionsPerBook;
                      
                      return (
                        <Box key={bookId} sx={{ mb: 0.5 }}>
                          • <strong>{book.title}</strong>: {availableQuestions}문제 
                          {isLimited && (
                            <span style={{ color: '#ff6b6b' }}>
                              (하이라이트 {book.highlightCount}개로 제한)
                            </span>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  <Typography sx={{ 
                    fontSize: '0.8rem', 
                    color: '#1a237e',
                    fontFamily: 'NanumSquareB',
                    mt: 1
                  }}>
                    총 {selectedBooks.reduce((total, bookId) => {
                      const book = userBooks.find(b => b.id === bookId);
                      return total + Math.min(questionsPerBook, book?.highlightCount || 0);
                    }, 0)}문제 출제 예정
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          <Box sx={{ px: 2 }}>
          {/* 안내 메시지 */}
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'NanumSquareEB', mt: 6}}>
            📚 책을 선택해주세요 (최소 3권)
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontFamily: 'NanumSquareR', mb: 3 }}>
            선택한 책의 문장이 문제로 출제됩니다.
          </Typography>

          {/* 책 선택 목록 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h7" sx={{ fontWeight: 700, fontFamily: 'NanumSquareB' }}>
              내 책 목록 ({userBooks.length}권)
            </Typography>
            
            {userBooks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, mt: 2 }}>
                <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                  등록된 책이 없습니다.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/add-book')}
                  sx={{ bgcolor: '#1a237e' }}
                >
                  책 추가하기
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {userBooks.map((book) => (
                  <Card
                    key={book.id}
                    elevation={0}
                    sx={{
                      border: selectedBooks.includes(book.id) ? '2px solid #1a237e' : '1px solid #e0e0e0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                    onClick={() => handleBookToggle(book.id)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedBooks.includes(book.id)}
                              sx={{
                                color: '#1a237e',
                                '&.Mui-checked': {
                                  color: '#1a237e',
                                },
                              }}
                            />
                          }
                          label=""
                          sx={{ mr: 1 }}
                        />
                        
                        <CardMedia
                          component="img"
                          sx={{
                            width: 60,
                            height: 90,
                            borderRadius: '4px',
                            mr: 2,
                            objectFit: 'cover',
                          }}
                          image={book.thumbnail || '/images/no-cover.png'}
                          alt={book.title}
                        />
                        
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              mb: 0.5,
                              fontFamily: 'NanumSquareB',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {book.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              mb: 0.5,
                              fontFamily: 'NanumSquareR',
                            }}
                          >
                            {book.authors?.join(', ') || '저자 정보 없음'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#1a237e',
                              fontWeight: 600,
                              fontFamily: 'NanumSquareB',
                            }}
                          >
                            문장 {book.highlightCount}개
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
          </Box>

          {/* 생성 버튼 */}
          <Box sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.default', pt: 2, pb: 2 }}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={selectedBooks.length < 3 || creating}
              onClick={() => setShowConfirmDialog(true)}
              sx={{
                bgcolor: '#1a237e',
                color: 'white',
                fontWeight: 700,
                borderRadius: '12px',
                py: 1.5,
                textTransform: 'none',
                fontFamily: 'NanumSquareB',
                fontSize: '1.1rem',
                '&:hover': {
                  bgcolor: '#283593',
                },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#999',
                },
              }}
            >
              {creating ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <CheckIcon sx={{ mr: 1 }} />
                  {selectedBooks.reduce((total, bookId) => {
                    const book = userBooks.find(b => b.id === bookId);
                    return total + Math.min(questionsPerBook, book?.highlightCount || 0);
                  }, 0)}문제로 테스트 만들기
                </>
              )}
            </Button>
          </Box>
        </Box>

        {/* 확인 다이얼로그 */}
        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, fontFamily: 'NanumSquareB' }}>
            테스트 생성 확인
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2, fontFamily: 'NanumSquareR' }}>
              선택한 {selectedBooks.length}권의 책으로 총 {selectedBooks.reduce((total, bookId) => {
                const book = userBooks.find(b => b.id === bookId);
                return total + Math.min(questionsPerBook, book?.highlightCount || 0);
              }, 0)}문제의 문장 테스트를 생성하시겠습니까?
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', fontFamily: 'NanumSquareR' }}>
              생성된 테스트는 친구들과 공유할 수 있습니다.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmDialog(false)}>
              취소
            </Button>
            <Button
              onClick={() => {
                setShowConfirmDialog(false);
                handleCreateTest();
              }}
              variant="contained"
              sx={{ bgcolor: '#1a237e' }}
            >
              생성하기
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default GameCreate; 